import React, { Ref } from 'react';
import classNames from 'classnames';
import { useVote } from './withVote';
import { isAF } from '../../lib/instanceSettings';
import { useVoteButtonsDisabled } from './useVoteButtonsDisabled';
import { VotingSystem } from '@/lib/voting/votingSystemTypes';
import { isFriendlyUI } from '../../themes/forumTheme';
import { TooltipRef, TooltipSpan } from '../common/FMTooltip';
import OverallVoteButton from "./OverallVoteButton";
import { Typography } from "../common/Typography";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles("PostsVoteDefault", (theme: ThemeType) => ({
  voteBlock: {
    width: 50,
  },
  voteBlockHorizontal: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  upvote: {
    marginBottom: -21
  },
  upvoteHorizontal: {
    marginTop: -8
  },
  downvote: {
    marginTop: -28
  },
  downvoteHorizontal: {
    marginTop: -6
  },
  voteScores: {
    margin:"15%",
    
    ...(theme.isLW && {
      margin: "25% 15% 15% 15%"
    }),
    ...(theme.isAF && {
      fontVariantNumeric: "lining-nums",
    }),
  },
  voteScoresHorizontal: {
    margin: '0 12px'
  },
  voteScore: {
    color: theme.palette.grey[500],
    position: 'relative',
    zIndex: theme.zIndexes.postsVote,
    fontSize: '55%'
  },
  voteScoreFooter: {
    fontSize: 18,
    fontWeight: 500,
  },
  voteScoreGoodHeart: {
    ...theme.typography.commentStyle,
    color: theme.palette.grey[700],
    fontSize: '45%',
    textAlign: "center",
  },
  secondaryVoteScore: {
    fontSize: '35%',
    marginBottom: 2,
  },
  tooltip: {
    color: theme.palette.grey[500],
    fontSize: '1rem',
    backgroundColor: theme.palette.panelBackground.default,
    transition: 'opacity 150ms cubic-bezier(0.4, 0, 1, 1) 0ms',
    marginLeft: 0,
    paddingTop: 0
  },
}));

const PostsVoteDefault = ({
  post,
  useHorizontalLayout,
  votingSystem,
  isFooter,
}: {
  post: PostsWithVotes,
  /** if true, display the vote arrows to the left & right of the score */
  useHorizontalLayout?: boolean,
  votingSystem?: VotingSystem<PostsWithVotes>,
  isFooter?: boolean,
}) => {
  const classes = useStyles(styles);
  const voteProps = useVote(post, "Posts", votingSystem);

  const {fail, reason: whyYouCantVote} = useVoteButtonsDisabled();
  const canVote = !fail;

  let tooltipPlacement: "left"|"right"|"top" = isFriendlyUI() ? "left" : "right";
  if (useHorizontalLayout) {
    tooltipPlacement = "top";
  }

  return (
    <div className={classNames({
      [classes.voteBlock]: !useHorizontalLayout,
      [classes.voteBlockHorizontal]: useHorizontalLayout,
    })}>
      <TooltipRef
        title={whyYouCantVote ?? "Click-and-hold for strong vote (click twice on mobile)"}
        placement={tooltipPlacement}
        popperClassName={classes.tooltip}
      >
        {(ref: Ref<HTMLDivElement>) => <div ref={ref}className={classNames({
          [classes.upvote]: !useHorizontalLayout,
          [classes.upvoteHorizontal]: useHorizontalLayout,
        })}>
          <OverallVoteButton
            orientation="up"
            color="secondary"
            upOrDown="Upvote"
            enabled={canVote}
            {...voteProps}
          />
        </div>}
      </TooltipRef>
      <div className={classNames({
        [classes.voteScores]: !useHorizontalLayout,
        [classes.voteScoresHorizontal]: useHorizontalLayout,
      })}>
        <TooltipSpan
          title={`${voteProps.voteCount} ${voteProps.voteCount === 1 ? "Vote" : "Votes"}`}
          placement={tooltipPlacement}
          popperClassName={classes.tooltip}
          hideOnTouchScreens
        >
          <Typography
            variant="headline"
            className={classNames(classes.voteScore, {
              [classes.voteScoreFooter]: isFooter,
            })}
          >
            {voteProps.baseScore}
          </Typography>
        </TooltipSpan>

        {!!post.af && !!post.afBaseScore && !isAF() &&
          <TooltipSpan
            title="AI Alignment Forum karma"
            placement={tooltipPlacement}
            popperClassName={classes.tooltip}
          >
            <Typography
              variant="headline"
              className={classNames(classes.voteScore, classes.secondaryVoteScore, {
                [classes.voteScoreFooter]: isFooter,
              })}>
              Ω {post.afBaseScore}
            </Typography>
          </TooltipSpan>
        }
      </div>
      <TooltipRef
        title={whyYouCantVote ?? "Click-and-hold for strong vote (click twice on mobile)"}
        placement={tooltipPlacement}
        popperClassName={classes.tooltip}
      >
        {(ref: Ref<HTMLDivElement>) => <div ref={ref} className={classNames({
          [classes.downvote]: !useHorizontalLayout,
          [classes.downvoteHorizontal]: useHorizontalLayout,
        })}>
          <OverallVoteButton
            orientation="down"
            color="error"
            upOrDown="Downvote"
            enabled={canVote}
            {...voteProps}
          />
        </div>}
      </TooltipRef>
    </div>
  );
}

export default PostsVoteDefault;


